// RagEditPopover — small modal-style picker for click-to-edit RAG.
// Used by both the My Accounts list (card RAG dot) and the map view
// (pin tap). Mobile-first: centered overlay with three big buttons
// G / A / R, plus an "Add reason" expander that reveals the category
// dropdown and free-text textarea.
//
// Why not an anchored popover? Phones are 380px wide — an absolutely-
// positioned popover anchored to a 10px dot ends up off-screen or
// covered. A centered overlay (with backdrop tap-to-dismiss) is the
// same gesture the AM already uses for the FeedbackForm and matches
// the rest of the module's "small full-screen sheet" pattern.
//
// 2026-07-06 — Financial RAG dimension removed entirely (we don't track
// it). This component is operational-RAG only now.
// ============================================================================
import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../shared/components';
import {
  RAG_CATEGORY_LABELS,
  RAG_COLORS,
  ragLabel,
} from '../../../shared/design-tokens';
import { saveRag } from '../lib/building-saves';
import { useAuth } from '../../../shared/auth';
import type { RagCategory, RagStatus } from '../../../shared/types';
import { overlayClickClose } from '../../../shared/overlay-close';

export interface RagEditPopoverProps {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  facilityName: string;
  currentRag: RagStatus | null;
  /**
   * Optimistic update before the network round-trip — parent rolls back
   * on save error by checking the error in onSaved's second argument.
   */
  onOptimistic: (next: {
    rag: RagStatus;
    category: RagCategory | null;
    reason: string | null;
  }) => void;
  /** Called after the UPDATE succeeds or fails. Parent refetches on error. */
  onSaved: (ok: boolean, errorMessage: string | null) => void;
}

const RAG_OPTIONS: RagStatus[] = ['Green', 'Amber', 'Red'];

export function RagEditPopover({
  open,
  onClose,
  facilityId,
  facilityName,
  currentRag,
  onOptimistic,
  onSaved,
}: RagEditPopoverProps) {
  const { employee } = useAuth();
  const [selected, setSelected] = useState<RagStatus | null>(currentRag);
  const [showReason, setShowReason] = useState(false);
  const [category, setCategory] = useState<RagCategory | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const canSave = selected != null && selected !== currentRag && !submitting;

  async function handleSave() {
    if (!selected || selected === currentRag) return;
    setSubmitting(true);
    setError(null);

    const cat = (category || null) as RagCategory | null;
    const reasonClean = reason.trim() || null;

    // Optimistic: paint the dot now, roll back on error.
    onOptimistic({ rag: selected, category: cat, reason: reasonClean });

    // The RAG's one save (lib/building-saves.ts), shared with the other RAG
    // editor and Ask AI's building card (item 944).
    const { error: updErr } = await saveRag({
      facilityId: facilityId,
      rag: selected,
      category: cat,
      reason: reasonClean,
      setByEid: employee?.employee_id ?? null,
    });

    setSubmitting(false);
    if (updErr) {
      setError(updErr.message);
      onSaved(false, updErr.message);
      return;
    }
    onSaved(true, null);
    onClose();
  }

  const heading = 'Change RAG';
  const categoryLabels = RAG_CATEGORY_LABELS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${heading} for ${facilityName}`}
      {...overlayClickClose(onClose)}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-tcs-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-tcs-text">{heading}</h2>
            <p className="text-xs text-tcs-text-muted">{facilityName}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="RAG status">
          {RAG_OPTIONS.map((s) => {
            const active = selected === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(s)}
                className={`flex h-14 flex-col items-center justify-center rounded-lg border-2 text-xs font-medium transition-colors ${
                  active
                    ? 'border-tcs-text bg-tcs-bg-muted'
                    : 'border-tcs-border bg-tcs-bg hover:bg-tcs-bg-muted'
                }`}
              >
                <span
                  className="mb-1 inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: RAG_COLORS[s] }}
                  aria-hidden="true"
                />
                {ragLabel[s]}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowReason((v) => !v)}
            className="text-xs text-tcs-text-secondary underline-offset-2 hover:text-tcs-text hover:underline"
          >
            {showReason ? 'Hide reason' : 'Add reason (optional)'}
          </button>
        </div>

        {showReason && (
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Category
              </span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as RagCategory | '')}
                className="h-10 w-full rounded-md border border-tcs-border-strong bg-tcs-bg px-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
              >
                <option value="">— Select —</option>
                {(Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>).map((k) => (
                  <option key={k as string} value={k as string}>
                    {categoryLabels[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Reason
              </span>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="What happened? (visible in the RAG history)"
                className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
              />
            </label>
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-md border border-rag-red/40 bg-rag-red/10 p-2 text-xs text-rag-red">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
